/* eslint-disable n/no-process-env */
import { createEnv } from "@t3-oss/env-nextjs";
import { boolean, minLength, number, optional, pipe, string, transform, unknown } from "valibot"; // we use valibot here bc we can get it very small since this will run on the browser. With minLength, pipe & string its less than 2kb pre minification

// Default cache period is 5 minutes (in seconds, for Next.js revalidate)
const DEFAULT_CACHE_PERIOD = 5 * 60;
const PAYLOAD_CACHE_PERIOD = process.env.ENABLE_CACHE_HANDLER === "true" ? 10 * 60 : DEFAULT_CACHE_PERIOD;
const DEFAULT_ASHBY_CACHE_PERIOD = DEFAULT_CACHE_PERIOD * 6;
const DEFAULT_XI_ASSETS_CACHE_PERIOD = 7 * 24 * 60 * 60; // 7 days

export const env = createEnv({
  server: {
    METRICS_PORT: optional(pipe(unknown(), transform(Number), number()), 9464),
    // Cache
    ENABLE_CACHE_HANDLER: optional(
      pipe(
        unknown(),
        transform(v => v === "true"),
        boolean(),
      ),
      false,
    ),
    CACHE_COMPRESSION: optional(
      pipe(
        unknown(),
        transform(v => v === "true"),
        boolean(),
      ),
      false,
    ),
    // Payload
    NEXT_PAYLOAD_USER_API_KEY: pipe(string(), minLength(1)),
    PAYLOAD_CMS_INTERNAL_URL: optional(pipe(string(), minLength(1))),

    // Ashby
    ASHBY_API_KEY: pipe(string(), minLength(1)),

    // PostHog - Secure API key for server-side feature flag evaluation
    POSTHOG_FEATURE_FLAGS_SECURE_API_KEY: pipe(string(), minLength(1)),
    // Local development only: JSON string to override feature flags, e.g. '{"mw-flag-name": true, "mw-other-flag": "variant-b"}'
    LOCAL_FEATURE_FLAG_OVERRIDES: optional(pipe(string())),

    // ElevenLabs
    INTERNAL_ENDPOINTS_OIDC_AUDIENCE: optional(pipe(string(), minLength(1))),
    // IAP OAuth2 client ID for the CMS backend — required when PAYLOAD_CMS_INTERNAL_URL is unset.
    PAYLOAD_CMS_IAP_AUDIENCE: optional(pipe(string(), minLength(1))),
    PROTECTED_ROUTE_GENERATOR_PASSWORD: optional(pipe(string()), ""),

    NEXT_PRIVATE_DRAFT_SECRET: pipe(string(), minLength(1)),
    // Next.js Server Actions - Required for consistent Server Action IDs across multiple server instances
    NEXT_SERVER_ACTIONS_ENCRYPTION_KEY: optional(pipe(string(), minLength(1))),

    // Google Cloud CDN
    GOOGLE_CLOUD_PROJECT: optional(pipe(string(), minLength(1))),
    GOOGLE_CLOUD_CDN_URL_MAP: optional(pipe(string(), minLength(1))),
    GOOGLE_APPLICATION_CREDENTIALS: optional(pipe(string(), minLength(1))),

    // HubSpot
    HUBSPOT_API_KEY: optional(pipe(string(), minLength(1))),

    // Asset translation (XI API content, e.g. sound effect prompts) - production only
    OPENAI_API_KEY: optional(pipe(string(), minLength(1))),
    ENABLE_ASSET_TRANSLATION: optional(pipe(string())),

    // Payload fetch timeout
    PAYLOAD_FETCH_TIMEOUT_MS: optional(pipe(unknown(), transform(Number), number()), 60_000),

    // Version/Build Information
    APP_VERSION: optional(pipe(string(), minLength(1))),
    REGION: optional(pipe(string(), minLength(1))),

    // Static generation
    STATIC_GENERATION_SIZE: optional(pipe(string(), minLength(1))),
  },

  client: {
    // Cache periods
    // I have a sneaking feeling that these two are not actually client env variables
    NEXT_PUBLIC_CACHE_PERIOD: optional(pipe(unknown(), transform(Number), number()), DEFAULT_CACHE_PERIOD),
    NEXT_PUBLIC_PAYLOAD_CACHE_PERIOD: optional(pipe(unknown(), transform(Number), number()), PAYLOAD_CACHE_PERIOD),
    NEXT_PUBLIC_ASHBY_CACHE_PERIOD: optional(pipe(unknown(), transform(Number), number()), DEFAULT_ASHBY_CACHE_PERIOD),

    // Ashby
    NEXT_PUBLIC_ASHBY_JOB_BOARD_BASE_URL: optional(pipe(string(), minLength(1)), "https://jobs.ashbyhq.com/elevenlabs"),

    // ElevenLabs
    NEXT_PUBLIC_XI_SERVER_URL: pipe(string(), minLength(1)),
    NEXT_PUBLIC_DISABLE_XI_FETCHES: optional(pipe(unknown(), transform(Number), number()), 0),
    NEXT_PUBLIC_XI_ASSETS_CACHE_PERIOD: optional(
      pipe(unknown(), transform(Number), number()),
      DEFAULT_XI_ASSETS_CACHE_PERIOD,
    ),
    NEXT_PUBLIC_PUBLIC_USER_ID: pipe(string(), minLength(1)),

    // General
    NEXT_PUBLIC_WEB_APP_URL: pipe(string(), minLength(1)),
    NEXT_PUBLIC_SERVER_URL: optional(pipe(string(), minLength(1)), "https://elevenlabs.io"),
    // IAP-protected public CMS URL. Server-side use requires PAYLOAD_CMS_IAP_AUDIENCE; prefer PAYLOAD_CMS_INTERNAL_URL on the internal network.
    NEXT_PUBLIC_CMS_URL: pipe(string(), minLength(1)),
    NEXT_PUBLIC_CDN_DOMAIN: pipe(string(), minLength(1)),
    NEXT_PUBLIC_MEDIA_SERVER_URL: optional(pipe(string(), minLength(1)), undefined),

    // PostHog
    NEXT_PUBLIC_POSTHOG_PUBLIC_KEY: pipe(string(), minLength(1)),

    // Cookiebot & GTM
    NEXT_PUBLIC_COOKIEBOT_DOMAIN_ID: pipe(string(), minLength(1)),
    NEXT_PUBLIC_GTM_ID: pipe(string(), minLength(1)),
    NEXT_PUBLIC_GTM_AUTH: pipe(string(), minLength(1)),
    NEXT_PUBLIC_GTM_ENV: pipe(string(), minLength(1)),

    // Firebase
    NEXT_PUBLIC_FIREBASE_DOMAIN: pipe(string(), minLength(1)),
    NEXT_PUBLIC_FIREBASE_API_KEY: pipe(string(), minLength(1)),
    NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN: pipe(string(), minLength(1)),
    NEXT_PUBLIC_FIREBASE_PROJECT_ID: pipe(string(), minLength(1)),
    NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET: pipe(string(), minLength(1)),
    NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID: pipe(string(), minLength(1)),
    NEXT_PUBLIC_FIREBASE_APP_ID: pipe(string(), minLength(1)),
    NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID: optional(pipe(string(), minLength(1))),

    // Feathery
    NEXT_PUBLIC_FEATHERY_SDK_KEY: pipe(string(), minLength(1)), // this is used for forms in /contact-sales and also /grants-application

    // Disable fetching docs from payload. This is used to speed up build for testing
    NEXT_PUBLIC_DISABLE_FETCH_ALL_DOCS: optional(pipe(unknown(), transform(Number), number()), 0),

    // hcaptcha
    NEXT_PUBLIC_HCAPTCHA_SITEKEY_ANON_REQUEST: pipe(string(), minLength(1)),

    // Google One Tap
    NEXT_PUBLIC_GOOGLE_CLIENT_ID: optional(pipe(string(), minLength(1))),
  },

  // We need to add this because nextjs will remove process.env variables from bundle if they are not used
  runtimeEnv: {
    METRICS_PORT: process.env.METRICS_PORT,
    // Cache
    ENABLE_CACHE_HANDLER: process.env.ENABLE_CACHE_HANDLER,
    CACHE_COMPRESSION: process.env.CACHE_COMPRESSION,
    // Payload
    NEXT_PAYLOAD_USER_API_KEY: process.env.NEXT_PAYLOAD_USER_API_KEY,
    PAYLOAD_CMS_INTERNAL_URL: process.env.PAYLOAD_CMS_INTERNAL_URL,
    NEXT_PRIVATE_DRAFT_SECRET: process.env.NEXT_PRIVATE_DRAFT_SECRET,
    NEXT_SERVER_ACTIONS_ENCRYPTION_KEY: process.env.NEXT_SERVER_ACTIONS_ENCRYPTION_KEY,

    // Ashby
    ASHBY_API_KEY: process.env.ASHBY_API_KEY,
    NEXT_PUBLIC_ASHBY_CACHE_PERIOD: process.env.NEXT_PUBLIC_ASHBY_CACHE_PERIOD,
    NEXT_PUBLIC_ASHBY_JOB_BOARD_BASE_URL: process.env.NEXT_PUBLIC_ASHBY_JOB_BOARD_BASE_URL,

    // PostHog
    POSTHOG_FEATURE_FLAGS_SECURE_API_KEY: process.env.POSTHOG_FEATURE_FLAGS_SECURE_API_KEY,
    LOCAL_FEATURE_FLAG_OVERRIDES: process.env.LOCAL_FEATURE_FLAG_OVERRIDES,
    NEXT_PUBLIC_POSTHOG_PUBLIC_KEY: process.env.NEXT_PUBLIC_POSTHOG_PUBLIC_KEY,

    // ElevenLabs
    NEXT_PUBLIC_DISABLE_XI_FETCHES: process.env.NEXT_PUBLIC_DISABLE_XI_FETCHES,
    INTERNAL_ENDPOINTS_OIDC_AUDIENCE: process.env.INTERNAL_ENDPOINTS_OIDC_AUDIENCE,
    PAYLOAD_CMS_IAP_AUDIENCE: process.env.PAYLOAD_CMS_IAP_AUDIENCE,
    NEXT_PUBLIC_XI_SERVER_URL: process.env.NEXT_PUBLIC_XI_SERVER_URL,
    NEXT_PUBLIC_XI_ASSETS_CACHE_PERIOD: process.env.NEXT_PUBLIC_XI_ASSETS_CACHE_PERIOD,
    NEXT_PUBLIC_PUBLIC_USER_ID: process.env.NEXT_PUBLIC_PUBLIC_USER_ID,
    PROTECTED_ROUTE_GENERATOR_PASSWORD: process.env.PROTECTED_ROUTE_GENERATOR_PASSWORD,

    // Google Cloud CDN
    GOOGLE_CLOUD_PROJECT: process.env.GOOGLE_CLOUD_PROJECT,
    GOOGLE_CLOUD_CDN_URL_MAP: process.env.GOOGLE_CLOUD_CDN_URL_MAP,
    GOOGLE_APPLICATION_CREDENTIALS: process.env.GOOGLE_APPLICATION_CREDENTIALS,

    // HubSpot
    HUBSPOT_API_KEY: process.env.HUBSPOT_API_KEY,

    // Asset translation
    OPENAI_API_KEY: process.env.OPENAI_API_KEY,
    ENABLE_ASSET_TRANSLATION: process.env.ENABLE_ASSET_TRANSLATION,

    // Cache periods
    NEXT_PUBLIC_CACHE_PERIOD: process.env.NEXT_PUBLIC_CACHE_PERIOD,
    NEXT_PUBLIC_PAYLOAD_CACHE_PERIOD: process.env.NEXT_PUBLIC_PAYLOAD_CACHE_PERIOD,

    // General
    NEXT_PUBLIC_WEB_APP_URL: process.env.NEXT_PUBLIC_WEB_APP_URL,
    NEXT_PUBLIC_SERVER_URL: process.env.NEXT_PUBLIC_SERVER_URL,
    NEXT_PUBLIC_CMS_URL: process.env.NEXT_PUBLIC_CMS_URL,
    NEXT_PUBLIC_CDN_DOMAIN: process.env.NEXT_PUBLIC_CDN_DOMAIN,
    NEXT_PUBLIC_MEDIA_SERVER_URL: process.env.NEXT_PUBLIC_MEDIA_SERVER_URL,
    NEXT_PUBLIC_DISABLE_FETCH_ALL_DOCS: process.env.NEXT_PUBLIC_DISABLE_FETCH_ALL_DOCS,

    // Cookiebot & GTM
    NEXT_PUBLIC_COOKIEBOT_DOMAIN_ID: process.env.NEXT_PUBLIC_COOKIEBOT_DOMAIN_ID,
    NEXT_PUBLIC_GTM_ID: process.env.NEXT_PUBLIC_GTM_ID,
    NEXT_PUBLIC_GTM_AUTH: process.env.NEXT_PUBLIC_GTM_AUTH,
    NEXT_PUBLIC_GTM_ENV: process.env.NEXT_PUBLIC_GTM_ENV,

    // hcaptcha
    NEXT_PUBLIC_HCAPTCHA_SITEKEY_ANON_REQUEST: process.env.NEXT_PUBLIC_HCAPTCHA_SITEKEY_ANON_REQUEST,

    // Feathery
    NEXT_PUBLIC_FEATHERY_SDK_KEY: process.env.NEXT_PUBLIC_FEATHERY_SDK_KEY,

    // Firebase
    NEXT_PUBLIC_FIREBASE_DOMAIN: process.env.NEXT_PUBLIC_FIREBASE_DOMAIN,
    NEXT_PUBLIC_FIREBASE_API_KEY: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
    NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
    NEXT_PUBLIC_FIREBASE_PROJECT_ID: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
    NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
    NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
    NEXT_PUBLIC_FIREBASE_APP_ID: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
    NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID,

    // Google One Tap
    NEXT_PUBLIC_GOOGLE_CLIENT_ID: process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID,

    // Payload fetch timeout
    PAYLOAD_FETCH_TIMEOUT_MS: process.env.PAYLOAD_FETCH_TIMEOUT_MS,

    // Version/Build Information
    APP_VERSION: process.env.APP_VERSION,
    REGION: process.env.REGION,

    // Static generation
    STATIC_GENERATION_SIZE: process.env.STATIC_GENERATION_SIZE,
  },

  // Skip validation during Storybook builds since env vars aren't needed
  skipValidation: !!process.env.SKIP_ENV_VALIDATION,

  /**
   * By default, this library will feed the environment variables directly to
   * the validator.
   *
   * This means that if you have an empty string for a value that is supposed
   * to be a number (e.g. `PORT=` in a ".env" file), Zod will incorrectly flag
   * it as a type mismatch violation. Additionally, if you have an empty string
   * for a value that is supposed to be a string with a default value (e.g.
   * `DOMAIN=` in an ".env" file), the default value will never be applied.
   *
   * In order to solve these issues, we recommend that all new projects
   * explicitly specify this option as true.
   */
  emptyStringAsUndefined: true,
});

export const IS_LOCAL = process.env.NODE_ENV === "development";
export const IS_PROD = process.env.NODE_ENV === "production";
export const IS_SERVER = typeof window === "undefined";
